export const getFirstOrNull = <T>(array: T[]): T | null => (array.length ? array[0] : null);
const SECURE_RANDOM_INT_DEFAULT = 999;

export const secureRandomInt = (max: number = SECURE_RANDOM_INT_DEFAULT): number => {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return array[0] % max;
};

export const shuffleArray = <T>(array: T[]): T[] => {
  const result = [ ...array ];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = secureRandomInt(i + 1);
    [ result[i], result[j] ] = [ result[j], result[i] ];
  }
  return result;
};

export const LIST_FORMATTER = new Intl.ListFormat("en", {
  style: "long",
  type: "conjunction",
});

export const LIST_FORMATTER_SHORT = new Intl.ListFormat("en", {
  style: "short",
  type: "conjunction",
});

type TPrimitive = string | number | bigint | boolean | symbol | null | undefined;

/** Use this to filter out non-distinct items from an array.
 * The ID must be a primitive, because this function uses === for the comparison.
 * For non-distinct elements, the first element will be returned.
 * @example
 * const input = [
 *   { id: 1, val: "one" },
 *   { id: 2, val: "two" },
 *   { id: 1, val: "uno" },
 * ];
 * const output = input.filter(distinctBy((it) => it.id)));
 * // The output array will contain the following:
 * const expectedOutput = [
 *   { id: 1, val: "one" },
 *   { id: 2, val: "two" },
 * ]; */
export function distinctBy<
  T,
  V extends TPrimitive,
>(
  idGetter: (item: T) => V,
): Parameters<Array<T>["filter"]>[0] {
  return (val, index, arr) => arr
    // An array item is distinct if every time it is encountered is
    // also the first time it is encountered in the array.
    // If this isn't the first time it is encountered, it will be filtered out.
    .findIndex((it) => idGetter(it) === idGetter(val)) === index;
}

export function filterWithNull<T, K extends keyof T>(
  array: T[],
  property: K,
): (T & { [P in K]: NonNullable<T[P]> })[] {
  return array.filter(
    (item): item is T & { [P in K]: NonNullable<T[P]> } => item[property] !== null,
  );
}

export const first = <T>(array: T[] | undefined): T | undefined => (array ? array[0] : undefined);
